import { DisplayPricingResult, Price, SimpleDisplayPrice } from 'shared-client/types/commerce/display-price';
import { UnitOfMass } from 'shared-client/types/commerce/product-details';
import { BreadcrumbItem } from 'shared/components/Breadcrumbs';
import { PartialDataLoaderOptions } from 'shared/data/usePartialDataLoader';
import { BasePartialDetailsState } from 'shared/types';
import { Asset } from 'shared/assets/types';

export function getInitialProductDetailsState(): ProductDetailsState {
	return window.__ROC_COMMERCE_PRODUCT_DETAILS__ ?? {};
}

declare global {
	interface Window {
		__ROC_COMMERCE_PRODUCT_DETAILS__: ProductDetailsState;
	}
}

export interface ProductDetailsState extends BasePartialDetailsState {
	summary: Summary;

	assets?: ProductAsset[] | null;

	availability?: Availability | null;

	brand?: Brand | null;

	/**
	 * Represents the visible tabs for the current product
	 */
	tabs?: TabViewChunk[];

	selections?: ProductDetailSelection[];

	children?: SimpleProduct[];

	relatedProducts?: RelatedProduct[];

	reviewsSummary: ReviewSummaryDto;

	/**
	 * The available pricing lines for the product as well as the selected pricing line state
	 */
	pricingLines?: ProductPricingLinesDetails;

	/**
	 * The pricing result tree containing renderable prices for the product.
	 */
	displayPricing?: DisplayPricingResult;

	breadcrumbPath?: BreadcrumbItem[];

	/**
	 * Information about the available product add ons for the product, and for child products, if this is a group
	 */
	addOns?: AddOnsPdpDto;

	isNewProduct: boolean;
}

/**
 * Single add on data.
 */
export interface ProductAddOnDto {
	/**
	 * The id of the product add on.
	 */
	value: string;

	/**
	 * The name of the product add on.
	 */
	label: string;

	/**
	 * The SKU of the product add on.
	 */
	sku: string;

	/**
	 * The description of the product add on.
	 */
	description: string;

	/**
	 * Represents if the current add on is required to add a product to cart.
	 */
	required: boolean;

	/**
	 * Represents if the current add on is selected
	 */
	selected: boolean;

	/**
	 * Represents if the current add on is updated
	 */
	isModified: boolean;

	/**
	 * Represents the add on attributes
	 */
	attributes: AddOnAttributeDto[];

	/**
	 * The add on main image thumbnail.
	 */
	thumbnailUrl: string;

	/**
	 * The add on main image alt text.
	 */
	altText: string;

	/**
	 * The displayable price of a single unit of this line item.
	 */
	unitPrice?: SimpleDisplayPrice<Price>;
}

/**
 * Product add on single attribute data
 */
export interface AddOnAttributeDto {
	/**
	 * Product add on attribute id
	 */
	value: string;

	/**
	 * Product add on attribute name
	 */
	label: string;

	/**
	 * Represents if the add on attribute value is required
	 */
	required: boolean;

	/**
	 * Product add on attribute instruction
	 */
	instructions: string;

	/**
	 * Product add on attribute input type
	 */
	inputAttributeType: InputAttributeType | null;

	/**
	 * The maximum length constraint for the add on attribute value
	 */
	maximumLength: number | null;

	/**
	 * The minimum length constraint for the add on attribute value
	 */
	minimumLength: number | null;

	/**
	 * The maximum number of line constraint for the add on attribute value
	 * used for multiline attributes
	 */
	maximumNumberOfLines: number | null;

	/**
	 * The default value of the add on attribute value
	 */
	defaultValue: string;

	/**
	 * The entered value for the current attribute
	 */
	data: string;

	/**
	 * The add on attribute selectable values
	 */
	values: AttributeValueDto[];
}

/**
 * The add on attribute input type
 */
export enum InputAttributeType {
	Single = 'Single',
	Multiline = 'Multiline',
}

/**
 * Single add on attribute selectable value
 */
export interface AttributeValueDto {
	/**
	 * The add on attribute selectable value id
	 */
	value: string;

	/**
	 * The add on attribute selectable value label
	 */
	label: string;

	/**
	 * Represents if the current selectable value is selected
	 */
	selected: boolean;
}

export interface Summary {
	name: string;
	type: ProductType;
	sku?: string;
	manufacturerPartNumber?: string;
	productUrl: string;
	description?: string;
	meta?: Meta;
	productGroupId: string | null;
	/**
	 * 	Flag defining if the current product should use the alternate product details.
	 */
	useAlternateProductDetails?: boolean;
	// #region HCL
	productGroupName?: string;
	guestQuoteEnabled?: boolean;
	// #endregion
}

export interface Meta {
	url: string;
	pageTitle?: string;
	metaKeywords?: string;
	metaDescription?: string;
	noIndex: boolean;
	noFollow: boolean;
}

export interface Availability {
	backOrder: boolean;
	outOfStock: boolean;
	// #region HCL
	callForAvailability: boolean;
	// #endregion
}

/**
 * Product Review Summary DTO
 */
export interface ReviewSummaryDto {
	/**
	 * The average rating of the product. If undefined, the product has no reviews with ratings.
	 */
	averageRating?: number;

	/**
	 * The number of public reviews for the product
	 */
	reviewCount: number;
}

export interface ProductAsset extends Asset {
	/**
	 * The alternate text associated with the image
	 */
	altText: string;
	/**
	 * The main image URL
	 */
	mainImageUrl: string;
	/**
	 * The thumbnail URL
	 */
	thumbnailUrl: string;
	/**
	 * The enlarged image URL
	 */
	enlargedImageUrl: string;
	/**
	 * The sort order for this image
	 */
	sortOrder: number;
	/**
	 * Embedded URL for external media asset
	 */
	embeddedUrl: string;
}

export interface Brand {
	name: string;
	logoUrl: string | null;
}

export interface TabViewChunk<T = unknown> {
	tab: Tab;
	model: T;
	rawHtml?: string;
}

export interface Tab {
	id: string;
	displayName: string;
	tabType: string;
	sortOrder: number;
	name: string;
}

export interface TabViewModel<T = unknown> {
	viewName: string;
	model: T;
}

export interface ProductDetailSelection {
	label: string;
	values: SelectionValue[];

	/**
	 * The selected attribute field name used for adding info to hawk context
	 */
	field: string;
}

/**
 * Represents a simple selection DTO object
 */
export interface SimpleSelectionDto {
	/**
	 * The Code of the product attribute
	 */
	id: string;

	/**
	 * The Name of the product attribute
	 */
	name: string;

	/**
	 * The Id of the attribute value
	 */
	value: string;

	/**
	 * The Name of the attribute value
	 */
	label: string;

	/**
	 * The selected attribute field name used for adding info to hawk context
	 */
	field: string;
}

export interface SelectionValue {
	value: string;
	label: string;
	valid: boolean;
	checked: boolean;
	// #region HCL
	swatchUrl: string;
	// #endregion
}

export interface SimpleProduct {
	id: string;
	name: string;
	//#region HCL
	sku: string;
	//#endregion
	selections: string[];
}

export interface RelatedProduct {
	id: string;
	name: string;
	description: string | null;
	url: string;
	mainImage: ProductAsset | null;
}

export interface ProductSelectionDispatcherDto {
	productId: string;
	productDetails: ProductDetailsState;
}

export interface AddOnsPdpDto {
	/**
	 * The list of available product add-ons for the product.
	 */
	productAddOnData?: ProductAddOnDto[];
	/**
	 * A list of add-ons available for child products, if this is a group product
	 */
	availableChildProductAddOns?: string[];
}

/*
	Shirt Size: Small, Large
	Color: Red, Green

	Scenario #1: All selections match

	Valid selections:
	Small, Red
	Small, Green
	Large, Red
	Large, Green

	1. Pick Size
	2. Pick Color
	3. Find match and load child

	Scenario #2: Selections mismatch

	Valid selections:
	Small, Red
	Small, Green
	Large, Red

	1. Pick Size (Small/Large)
	2. if Large, mark green as invalid
	3. if Small, show all colors
	4. Find match and load child

	Scenario #3: Selections mismatch 2

	Valid selections:
	Small, Red
	Small, Green
	Large, Red

	1. Pick Green, mark large as invalid
	2. Pick Size
	3. Find match and load child

	Scenario #4: Selections mismatch 3

	Valid selections:
	Small, Red
	Small, Green
	Large, Red

	1. Pick Green, mark large as invalid
	2. Pick Large anyway, remove color selection
	3. ...
	4. Find match and load child

*/

/**
 * All available product types
 *
 * @export
 */
export enum ProductType {
	Simple = 'Simple',
	Group = 'Group',
	Kit = 'Kit',
	GiftCard = 'GiftCard',
}

export type ProductPartialDataLoaderOptions = Pick<
	PartialDataLoaderOptions<ProductDetailsState>,
	'initialState' | 'dataLoaderFn'
>;

export interface ProductSelection {
	/**
	 * The code of the product attribute
	 */
	id: string;
	/**
	 * The name of the product attribute
	 */
	name: string;
	/**
	 * The attribute value name (if available), otherwise attribute value value
	 */
	label: string;
	/**
	 * The id (Guid) of the attribute value
	 */
	value: string;

	/**
	 * The selected attribute field name used for adding info to hawk context
	 */
	field: string;
}

/**
 * Base unit of measure
 */
export interface UnitOfMeasure {
	name: string;
	displayName: string;
}

/**
 * Represents a single pricing line for a product (one of potentially many choices for purchasing a product)
 */
export type ProductPricingLine = {
	/**
	 * The pricing line's id
	 */
	id: string;

	/**
	 *
	 * Unit of measure id
	 */
	unitOfMeasureId: string;

	/**
	 * Base Unit of measure
	 */
	unitOfMeasure: UnitOfMeasure;

	/**
	 * The unit's of measure display name
	 */
	label: string;

	/**
	 * The unit's of measure quantity
	 */
	quantity: number;

	/**
	 * If this pricing line isn't available for purchase, this will contain a string-localizer-key explaining the reason why.
	 */
	unavailabilityReason: string | undefined;
} & MassPricingLineOptions;

type MassPricingLineOptions =
	| {
			/**
			 * The unit's of measure weight unit
			 */
			unitOfMass: UnitOfMass;

			/**
			 * The unit's of measure weight
			 */
			weight: number;
	  }
	| {
			unitOfMass?: never;
			weight?: never;
	  };

/**
 * Represents state details pricing lines and pricing line selections for a product on a product details page.
 */
export interface ProductPricingLinesDetails {
	/**
	 * The selected pricing lines
	 */
	selected?: ProductPricingLine;
	/**
	 * The array of available pricing lines
	 */
	available: ProductPricingLine[];
}

/**
 * Reason that the exception was thrown when adding an item to the cart
 */
export enum AddToCartExceptionCode {
	generic = 'Generic',
	cartFull = 'CartFull',
	productUnavailable = 'ProductUnavailable',
	productOutOfStock = 'ProductOutOfStock',
}

/**
 * Interface used as request for getting bulk products partial data
 */
export interface BaseBulkPartialProductsDataOptions<T = unknown> {
	productIds: string[] | undefined;
	missingKeys: Array<keyof T>;
}
